import { useDndContext } from "@dnd-kit/core";
import type { ESidebarView } from "@frontend-shared/types";
import { isSidebarView, SIDEBAR_VIEWS } from "@frontend-shared/types";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { memo, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

import { SidebarViewTab } from "./components/SidebarViewTab";
import {
  SIDEBAR_VIEW_LABEL_KEYS,
  SIDEBAR_VIEW_TOUR_IDS,
  SPRING_LOAD_DELAY_MS,
} from "./constants/sidebar-view-switch";

interface ISidebarViewSwitchProps {
  onSpringLoad: (view: ESidebarView) => void;
  // NOTE: NSAI-6771 — only the views the member may open, from
  // `getSidebarViews`; the Assistant chats tab is absent without assistants
  // read access.
  views: readonly ESidebarView[];
}

/**
 * NOTE: NSAI-6342 — the chat sidebar's three panes — chats, assistant chats and
 * folders — as tabs instead of three stacked, collapsible sections. Stacked,
 * the folders and the assistants pushed the chat history down the pane by as
 * much as they happened to contain, so how much history you could see depended
 * on how many folders you had; a member with a handful of each had to scroll
 * past both to reach yesterday's chat. One pane at a time gives each list the
 * whole scrollport and gives the sidebar one heading instead of three.
 *
 * It is the same split mobile ships (NSAI-5622), down to the shared
 * `ESidebarView` order and i18n keys, so the two clients name and order the
 * panes identically.
 *
 * The row is sticky: it is the pane's own heading, and a heading that scrolls
 * away means scrolling back up to change panes.
 *
 * It is also where a drag crosses between panes. With folders in a pane of
 * their own, a chat dragged in the chats pane has no folder on screen to land
 * on; holding the drag over the Folders tab opens that pane mid-drag — the
 * pattern file managers call spring-loaded folders — so dragging a chat into a
 * folder survives the split. The tab is a signpost, not a destination: it is
 * deliberately not a dnd-kit droppable, so releasing on it is a no-op rather
 * than a surprise move. Which tab the pointer is over is read from the pointer
 * itself, because dnd-kit's rect collision would hand a full-width dragged row
 * to whichever tab happened to overlap it most rather than to the one under
 * the cursor.
 */
const SidebarViewSwitchComponent = ({
  onSpringLoad,
  views,
}: ISidebarViewSwitchProps): JSX.Element => {
  const { t } = useTranslation("sidebar");
  const { active } = useDndContext();
  const listRef = useRef<HTMLDivElement | null>(null);
  const [dragOverView, setDragOverView] = useState<ESidebarView | null>(null);

  const isChatDragActive = active?.data.current?.type === "chat";

  useEffect(() => {
    if (!isChatDragActive) {
      setDragOverView(null);
      return undefined;
    }

    const handlePointerMove = (event: PointerEvent): void => {
      const listElement = listRef.current;
      if (!listElement) return;

      const tabElement = Array.from(
        listElement.querySelectorAll<HTMLElement>("[data-sidebar-view]"),
      ).find((element) => {
        const rect = element.getBoundingClientRect();

        return (
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom
        );
      });
      const nextView = tabElement?.dataset.sidebarView;

      setDragOverView(isSidebarView(nextView) ? nextView : null);
    };

    document.addEventListener("pointermove", handlePointerMove);

    return (): void => {
      document.removeEventListener("pointermove", handlePointerMove);
      setDragOverView(null);
    };
  }, [isChatDragActive]);

  useEffect(() => {
    if (!dragOverView) return undefined;

    // NOTE: long enough that crossing the row on the way somewhere else does
    // not flip the pane under the pointer, short enough to feel like a
    // hover-to-open rather than a wait.
    const timeoutId = globalThis.setTimeout(() => {
      onSpringLoad(dragOverView);
    }, SPRING_LOAD_DELAY_MS);

    return (): void => {
      globalThis.clearTimeout(timeoutId);
    };
  }, [dragOverView, onSpringLoad]);

  return (
    // NOTE: the negative inline margin lets the sticky backdrop span the full
    // sidebar width, so the list scrolling underneath is covered edge to edge
    // rather than peeking through the group's own gutter.
    <div className="sticky top-0 z-20 -mx-1.5 bg-sidebar px-1.5 pt-1">
      <TabsPrimitive.List
        ref={listRef}
        // NOTE: the hairline under the row is what makes the tabs read as the
        // top of the pane below them rather than as a floating control.
        //
        // NOTE: NSAI-6771 — the full row spreads its tabs across the pane; a
        // shortened one (no Assistant chats without assistants access) packs
        // them at the start instead, since two tabs pushed to opposite edges
        // read as unrelated controls rather than one switch.
        className={cn(
          "flex w-full items-stretch border-b border-sidebar-border",
          views.length < SIDEBAR_VIEWS.length
            ? "justify-start gap-3"
            : "justify-between gap-0.5",
        )}
        data-testid="sidebar-view-switch"
      >
        {views.map((view) => (
          <SidebarViewTab
            key={view}
            view={view}
            label={t(SIDEBAR_VIEW_LABEL_KEYS[view])}
            tourId={SIDEBAR_VIEW_TOUR_IDS[view]}
            isDragOver={dragOverView === view}
          />
        ))}
      </TabsPrimitive.List>
    </div>
  );
};

export const SidebarViewSwitch = memo(SidebarViewSwitchComponent);
